import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { Plus, Pencil, Trash2, Filter, Search, ChevronDown, ChevronUp, Loader2, Tag, Zap, Palette, Calendar, Power } from 'lucide-react'
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Textarea,
  Separator,
} from '@jira-clone/ui'
import { useAssumptionsExclusions, useAssumptionExclusionMutations } from '../../shared/hooks/useAssumptionsExclusions'
import { useQuotationDefaults, useQuotationDefaultMutations } from '../../shared/hooks/useQuotationDefaults'
import { getApiErrorMessage } from '../../shared/utils/apiError'

const CATEGORY_LABELS: Record<string, string> = {
  Assumption: 'Assumption',
  Exclusion: 'Exclusion',
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Assumption: <Tag className="h-3.5 w-3.5" />,
  Exclusion: <Tag className="h-3.5 w-3.5" />,
}

const CATEGORY_COLORS: Record<string, string> = {
  Assumption: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  Exclusion: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800',
}

interface DraftState {
  id?: string
  title: string
  description: string
  category: 'Assumption' | 'Exclusion'
  isDefault: boolean
  isActive: boolean
}

const EMPTY_DRAFT: DraftState = {
  title: '',
  description: '',
  category: 'Assumption',
  isDefault: false,
  isActive: true,
}

export default function AssumptionExclusionAdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryParam = searchParams.get('category') as 'Assumption' | 'Exclusion' | 'all' | null
  const initialCategory = categoryParam && ['Assumption', 'Exclusion', 'all'].includes(categoryParam) ? categoryParam : 'all'
  const [activeCategory, setActiveCategoryState] = useState(initialCategory)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>(searchParams.get('isActive') as any || 'all')

  useEffect(() => {
    if (categoryParam && ['Assumption', 'Exclusion', 'all'].includes(categoryParam)) {
      setActiveCategoryState(categoryParam)
    } else if (!categoryParam) {
      setActiveCategoryState('all')
    }
  }, [categoryParam])

  const handleCategoryChange = (cat: 'Assumption' | 'Exclusion' | 'all') => {
    setActiveCategoryState(cat)
    setSearchParams(cat === 'all' ? {} : { category: cat })
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchParams(value ? { search: value, ...(activeCategory !== 'all' ? { category: activeCategory } : {}) } : 
      activeCategory !== 'all' ? { category: activeCategory } : {})
  }

  const handleIsActiveChange = (value: 'all' | 'true' | 'false') => {
    setIsActiveFilter(value)
    setSearchParams(value === 'all' ? { ...(activeCategory !== 'all' ? { category: activeCategory } : {}) } : 
      { isActive: value, ...(activeCategory !== 'all' ? { category: activeCategory } : {}) })
  }

  const { data: items = [], isLoading } = useAssumptionsExclusions({
    category: activeCategory === 'all' ? undefined : activeCategory,
    isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'true',
  })
  
  const { create, update, setStatus, remove, setDefault } = useAssumptionExclusionMutations()

  // Master-data switch: show/hide the Assumptions & Exclusions section in quotation and proforma forms
  const queryClient = useQueryClient()
  const { data: defaultsList = [] } = useQuotationDefaults()
  const defaultsDoc = defaultsList[0] as any
  const assumptionsEnabled = defaultsDoc?.assumptionsExclusionsEnabled === true
  const { update: updateDefaults } = useQuotationDefaultMutations()
  const [togglingSection, setTogglingSection] = useState(false)

  const handleToggleSection = async () => {
    setTogglingSection(true)
    try {
      await updateDefaults.mutateAsync({
        id: defaultsDoc?.id,
        payload: { assumptionsExclusionsEnabled: !assumptionsEnabled },
      })
      queryClient.invalidateQueries({ queryKey: ['documentMasterData'] })
      queryClient.invalidateQueries({ queryKey: ['quotationMasterConfig'] })
      toast.success(assumptionsEnabled
        ? 'Assumptions & Exclusions disabled — hidden from quotation and proforma forms.'
        : 'Assumptions & Exclusions enabled — shown in quotation and proforma forms.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'update the Assumptions & Exclusions setting'))
    } finally {
      setTogglingSection(false)
    }
  }

  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const openCreate = () => {
    setDraft(EMPTY_DRAFT)
    setDialogOpen(true)
  }

  const openEdit = (item: any) => {
    setDraft({
      id: item.id,
      title: item.title,
      description: item.description || '',
      category: item.category,
      isDefault: item.isDefault,
      isActive: item.isActive,
    })
    setDialogOpen(true)
  }

  const handleSave = async () => {
    if (!draft.title.trim()) {
      toast.error('Title is required.')
      return
    }
    if (!draft.description.trim()) {
      toast.error('Description is required.')
      return
    }
    try {
      const payload = {
        title: draft.title.trim(),
        description: draft.description.trim(),
        category: draft.category,
        isDefault: draft.isDefault,
        isActive: draft.isActive,
      }
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, payload })
        toast.success('Item updated.')
      } else {
        await create.mutateAsync(payload)
        toast.success('Item created.')
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, draft.id ? 'update this item' : 'create this item'))
    }
  }

  const handleDelete = async (item: any) => {
    if (!confirm(`Delete "${item.title}"? This can't be undone.`)) return
    setDeletingId(item.id)
    try {
      await remove.mutateAsync(item.id)
      toast.success('Item deleted.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this item'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleToggleActive = async (item: any) => {
    try {
      await setStatus.mutateAsync({ id: item.id, isActive: !item.isActive })
      toast.success(item.isActive ? 'Item deactivated.' : 'Item activated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle item status'))
    }
  }

  const handleSetDefault = async (item: any) => {
    try {
      await setDefault.mutateAsync(item.id)
      toast.success('Default item updated.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'set as default'))
    }
  }

  const categories: ('all' | 'Assumption' | 'Exclusion')[] = ['all', 'Assumption', 'Exclusion']

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-foreground">Assumptions & Exclusions</h2>
          <p className="text-sm text-muted-foreground">Manage standard assumptions and exclusions for quotations.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 mr-1">
            <Badge variant={assumptionsEnabled ? 'default' : 'secondary'} className="text-xs">
              {assumptionsEnabled ? 'Enabled in forms' : 'Disabled in forms'}
            </Badge>
            <Button
              variant="outline"
              onClick={handleToggleSection}
              disabled={togglingSection}
              className="gap-1.5"
              title={assumptionsEnabled
                ? 'Hide the Assumptions & Exclusions fields from quotation and proforma forms'
                : 'Show the Assumptions & Exclusions fields in quotation and proforma forms'}
            >
              {togglingSection ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Power className="h-4 w-4" />
              )}
              {assumptionsEnabled ? 'Disable' : 'Enable'}
            </Button>
          </div>
          <Button onClick={openCreate} className="gap-1.5">
            <Plus className="h-4 w-4" /> New Item
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Category Tabs */}
            <div className="flex items-center gap-1 bg-muted p-1 rounded-lg">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    activeCategory === cat
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {cat !== 'all' && <span style={{ 
                    color: cat === 'Assumption' ? '#3b82f6' : '#ef4444'
                  }}>{CATEGORY_ICONS[cat]}</span>}
                  {cat === 'all' ? 'All' : CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>

            <Separator className="h-6 w-px" />

            {/* Active Filter */}
            <Select value={isActiveFilter} onValueChange={handleIsActiveChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No items yet. Create the first one.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-medium shrink-0 border ${CATEGORY_COLORS[item.category]}`}
                      >
                        {CATEGORY_ICONS[item.category]}
                      </span>
                      <Badge variant={item.isActive ? 'default' : 'secondary'} className="text-xs">
                        {item.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      {item.isDefault && (
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                          Default
                        </Badge>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-foreground truncate">{item.title}</p>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} title="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {item.isDefault ? (
                      <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary">
                        Default
                      </Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSetDefault(item)}
                        title="Set as Default"
                      >
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(item)}
                      title={item.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {item.isActive ? (
                        <span className="h-3.5 w-3.5 rounded-full bg-green-500" />
                      ) : (
                        <span className="h-3.5 w-3.5 rounded-full bg-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(item)}
                      disabled={deletingId === item.id}
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Item' : 'New Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ae-title">Title *</Label>
                <Input
                  id="ae-title"
                  value={draft.title}
                  onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                  placeholder="e.g. Client to provide all content"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ae-category">Category *</Label>
                <Select value={draft.category} onValueChange={(v) => setDraft((d) => ({ ...d, category: v as any }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASSUMPTION">Assumption</SelectItem>
                    <SelectItem value="EXCLUSION">Exclusion</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ae-description">Description *</Label>
              <Textarea
                id="ae-description"
                value={draft.description}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                placeholder="Detailed description"
                rows={3}
              />
            </div>

            <Separator className="my-2" />

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  id="ae-default"
                  type="checkbox"
                  checked={draft.isDefault}
                  onChange={(e) => setDraft((d) => ({ ...d, isDefault: e.target.checked }))}
                />
                <label htmlFor="ae-default" className="cursor-pointer">Set as default</label>
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                />
                <span>Active</span>
              </Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {draft.id ? 'Save Changes' : 'Create Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}